import { Suspense, useEffect, useRef, useState, useCallback } from 'react'
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { OrbitControls, Stars, Environment } from '@react-three/drei'
import { EffectComposer, Bloom } from '@react-three/postprocessing'
import * as THREE from 'three'
import { ChristmasTree } from './components/Scene/ChristmasTree'
import { Snowfall } from './components/Effects/Snowfall'
import { Ground } from './components/Scene/Ground'
import { HandGestureProvider } from './components/Camera/HandGestureProvider'
import { UIOverlay } from './components/UI/UIOverlay'
import { LoadingScreen } from './components/UI/LoadingScreen'
import { BackgroundMusic } from './components/UI/BackgroundMusic'
import { useTreeStore } from './store/useTreeStore'
import { SCENE_CONFIG, BLOOM_CONFIG, ANIMATION_CONFIG } from './utils/constants'

// 检测是否为移动设备
const isMobile = () => {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768
}

// 从SCENE_CONFIG提取摄像机位置 - 移动端和电脑端都使用更远的视角
const getInitialCameraPosition = (): [number, number, number] => {
  if (isMobile()) {
    // 移动端：视角拉远，能看到完整的圣诞树
    return [0, 6, 28]
  }
  // 电脑端：也使用较远的视角（接近最大距离30）
  return [0, 6, 28]
}

// 动画控制器组件 - 支持三种模式
function AnimationController() {
  const {
    setTreeState,
    setProgress,
    setGalleryProgress
  } = useTreeStore()
  
  useEffect(() => {
    // 动画循环
    let animationFrame: number
    
    const animate = () => {
      const { treeState, targetState, progress, galleryProgress } = useTreeStore.getState()
      const speed = ANIMATION_CONFIG.transitionSpeed / 60 // 假设60fps
      
      // 处理 FORMED <-> CHAOS 过渡
      if (targetState === 'FORMED') {
        if (progress < 1) {
          const newProgress = Math.min(1, progress + speed)
          setProgress(newProgress)
          if (treeState !== 'TRANSITIONING' && treeState !== 'FORMED') {
            setTreeState('TRANSITIONING')
          }
          if (newProgress === 1) {
            setTreeState('FORMED')
          }
        }
      } else if (targetState === 'CHAOS') {
        if (progress > 0) {
          const newProgress = Math.max(0, progress - speed)
          setProgress(newProgress)
          if (treeState !== 'TRANSITIONING' && treeState !== 'CHAOS') {
            setTreeState('TRANSITIONING')
          }
          if (newProgress === 0) {
            setTreeState('CHAOS')
          }
        }
        // 同时确保画廊进度归零
        if (galleryProgress > 0) {
          const newGalleryProgress = Math.max(0, galleryProgress - speed)
          setGalleryProgress(newGalleryProgress)
        }
      } else if (targetState === 'GALLERY') {
        // 先确保展开状态完成
        if (progress > 0) {
          const newProgress = Math.max(0, progress - speed)
          setProgress(newProgress)
        } else {
          // 然后进入画廊模式
          if (galleryProgress < 1) {
            const newGalleryProgress = Math.min(1, galleryProgress + speed)
            setGalleryProgress(newGalleryProgress)
            if (treeState !== 'TRANSITIONING' && treeState !== 'GALLERY') {
              setTreeState('TRANSITIONING')
            }
            if (newGalleryProgress === 1) {
              setTreeState('GALLERY')
            }
          }
        }
      }
      
      animationFrame = requestAnimationFrame(animate)
    }
    
    animationFrame = requestAnimationFrame(animate)
    
    return () => {
      cancelAnimationFrame(animationFrame)
    }
  }, [setTreeState, setProgress, setGalleryProgress])
  
  return null
}

// 摄像机控制器 - 包含画廊模式下的摄像机重置（丝滑过渡）
function CameraController() {
  const { targetState, galleryProgress, handCount } = useTreeStore()
  const { camera } = useThree()
  const controlsRef = useRef<any>(null)
  const prevTargetState = useRef(targetState)
  const isTransitioning = useRef(false)
  const targetCameraPosition = useRef(new THREE.Vector3(0, 4, 20))
  const isMobileDevice = isMobile()
  
  // 画廊模式下完全禁用 OrbitControls
  // 只有在检测到手的时候才禁用鼠标控制（handCount > 0）
  // 这样当没有手在画面中时，用户可以用鼠标控制视角
  // 移动端始终启用触控
  const isGalleryMode = targetState === 'GALLERY' || galleryProgress > 0
  const isActivelyTracking = !isMobileDevice && handCount > 0 // 移动端不受手势追踪影响
  const shouldDisable = isActivelyTracking || isGalleryMode
  
  // 当进入画廊模式时，开始丝滑过渡到正面位置
  useEffect(() => {
    if (targetState === 'GALLERY' && prevTargetState.current !== 'GALLERY') {
      // 设置目标位置，开始过渡
      targetCameraPosition.current.set(0, 4, 20)
      isTransitioning.current = true
    }
    prevTargetState.current = targetState
  }, [targetState])
  
  // 使用 useFrame 实现丝滑过渡（较慢的速度）
  useFrame(() => {
    if (isGalleryMode && isTransitioning.current) {
      // 丝滑插值到目标位置（0.03 = 较慢的过渡速度）
      camera.position.lerp(targetCameraPosition.current, 0.03)
      
      // 丝滑插值 lookAt 目标
      const currentLookAt = new THREE.Vector3()
      camera.getWorldDirection(currentLookAt)
      const targetLookAt = new THREE.Vector3(0, 4, 0).sub(camera.position).normalize()
      currentLookAt.lerp(targetLookAt, 0.03)
      camera.lookAt(camera.position.clone().add(currentLookAt.multiplyScalar(10)))
      
      // 检查是否接近目标位置
      if (camera.position.distanceTo(targetCameraPosition.current) < 0.1) {
        camera.position.copy(targetCameraPosition.current)
        camera.lookAt(0, 4, 0)
        isTransitioning.current = false
      }
      
      // 更新 OrbitControls
      if (controlsRef.current) {
        controlsRef.current.target.set(0, 4, 0)
        controlsRef.current.update()
      }
    }
  })
  
  return (
    <OrbitControls
      ref={controlsRef}
      enabled={!shouldDisable}
      enablePan={false}
      enableRotate={!isGalleryMode} // 画廊模式下禁用旋转
      enableZoom={!isGalleryMode} // 画廊模式下禁用缩放
      minDistance={isMobileDevice ? 15 : 10} // 移动端最小距离更远
      maxDistance={isMobileDevice ? 35 : 30} // 移动端最大距离更远
      minPolarAngle={Math.PI / 6}
      maxPolarAngle={Math.PI / 2}
      target={[0, 4, 0]}
      // 移动端触控优化
      rotateSpeed={isMobileDevice ? 0.5 : 1}
      zoomSpeed={isMobileDevice ? 0.5 : 1}
    />
  )
}

// 通用手势处理 - 双击、滑动、双指捏合（移动端和电脑端共用）
function GestureHandler() {
  const { targetState, setTargetState, nextPhoto, prevPhoto, handCount } = useTreeStore()
  const lastTapTime = useRef(0)
  const lastClickTime = useRef(0)
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)
  const initialPinchDistance = useRef(0)
  const isMobileDevice = isMobile()
  
  // 移动端触摸手势处理
  useEffect(() => {
    if (!isMobileDevice) return
    
    // 触摸开始 - 记录起始位置
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 1) {
        touchStartX.current = event.touches[0].clientX
        touchStartY.current = event.touches[0].clientY
      } else if (event.touches.length === 2) {
        // 双指触摸 - 记录初始距离
        const dx = event.touches[0].clientX - event.touches[1].clientX
        const dy = event.touches[0].clientY - event.touches[1].clientY
        initialPinchDistance.current = Math.sqrt(dx * dx + dy * dy)
      }
    }
    
    // 触摸结束 - 处理双击和滑动
    const handleTouchEnd = (event: TouchEvent) => {
      const currentTime = Date.now()
      const tapLength = currentTime - lastTapTime.current
      
      // 画廊模式下的处理
      if (targetState === 'GALLERY') {
        // 画廊模式下的单指滑动切换照片
        if (event.changedTouches.length === 1) {
          const touchEndX = event.changedTouches[0].clientX
          const touchEndY = event.changedTouches[0].clientY
          const deltaX = touchEndX - touchStartX.current
          const deltaY = touchEndY - touchStartY.current
          
          // 计算滑动距离
          const swipeDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
          
          // 滑动距离超过50px才触发切换照片
          if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX > 0) {
              // 向右滑动 - 上一张
              prevPhoto()
            } else {
              // 向左滑动 - 下一张
              nextPhoto()
            }
          } else if (swipeDistance < 20) {
            // 如果几乎没有滑动（点击），检测双击退出画廊
            if (tapLength < 300 && tapLength > 0) {
              event.preventDefault()
              // 双击空白处跳回展开模式（CHAOS），禁止直接跳到圣诞树
              setTargetState('CHAOS')
            }
          }
        }
        lastTapTime.current = currentTime
        return
      }
      
      // 非画廊模式下的双击检测（300ms内两次点击）
      if (tapLength < 300 && tapLength > 0) {
        event.preventDefault()
        
        // 圣诞树 <-> 展开 双向切换
        if (targetState === 'FORMED') {
          setTargetState('CHAOS')
        } else if (targetState === 'CHAOS') {
          setTargetState('FORMED')
        }
      }
      
      lastTapTime.current = currentTime
    }
    
    // 触摸移动 - 检测双指捏合
    const handleTouchMove = (event: TouchEvent) => {
      // 画廊模式下检测双指捏合退出
      if (targetState === 'GALLERY' && event.touches.length === 2) {
        const dx = event.touches[0].clientX - event.touches[1].clientX
        const dy = event.touches[0].clientY - event.touches[1].clientY
        const currentDistance = Math.sqrt(dx * dx + dy * dy)
        
        // 如果双指距离缩小超过100px，退出画廊模式
        if (initialPinchDistance.current - currentDistance > 100) {
          setTargetState('CHAOS')
          initialPinchDistance.current = currentDistance // 重置，防止重复触发
        }
      }
    }
    
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', handleTouchEnd)
    window.addEventListener('touchmove', handleTouchMove, { passive: true })
    
    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('touchmove', handleTouchMove)
    }
  }, [isMobileDevice, targetState, setTargetState, nextPhoto, prevPhoto])
  
  // 电脑端鼠标双击处理 - 仅在没有手势追踪时启用
  useEffect(() => {
    if (isMobileDevice) return
    
    // 如果正在手势追踪（有手在画面中），禁用鼠标双击
    if (handCount > 0) return
    
    const handleDoubleClick = (event: MouseEvent) => {
      const currentTime = Date.now()
      const clickLength = currentTime - lastClickTime.current
      
      // 双击检测（300ms内两次点击）
      if (clickLength < 300 && clickLength > 0) {
        event.preventDefault()
        
        // 画廊模式下双击跳回展开模式（CHAOS），禁止直接跳到圣诞树模式
        if (targetState === 'GALLERY') {
          setTargetState('CHAOS')
          return
        }
        
        // 圣诞树 <-> 展开 双向切换
        if (targetState === 'FORMED') {
          setTargetState('CHAOS')
        } else if (targetState === 'CHAOS') {
          setTargetState('FORMED')
        }
      }
      
      lastClickTime.current = currentTime
    }
    
    window.addEventListener('click', handleDoubleClick)
    return () => window.removeEventListener('click', handleDoubleClick)
  }, [isMobileDevice, targetState, setTargetState, handCount])
  
  return null
}

// 画廊模式下的鼠标点击处理
function GalleryClickHandler() {
  const { targetState, nextPhoto, prevPhoto } = useTreeStore()
  const { size } = useThree()
  
  useEffect(() => {
    if (targetState !== 'GALLERY') return
    
    const handleClick = (event: MouseEvent) => {
      // 计算点击位置是在屏幕左半边还是右半边
      const x = event.clientX
      const centerX = size.width / 2
      
      if (x < centerX) {
        // 点击左边，上一张
        prevPhoto()
      } else {
        // 点击右边，下一张
        nextPhoto()
      }
    }
    
    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [targetState, nextPhoto, prevPhoto, size.width])
  
  return null
}

// 加载指示器
function LoadingFallback() {
  return (
    <mesh>
      <sphereGeometry args={[1, 16, 16]} />
      <meshBasicMaterial color="#FFD700" wireframe />
    </mesh>
  )
}

// 场景内容组件 - 包含所有3D元素，并在加载完成后通知父组件
function SceneContent({ onReady }: { onReady: () => void }) {
  const hasNotified = useRef(false)
  
  useEffect(() => {
    // 延迟一帧确保场景已渲染
    const timer = setTimeout(() => {
      if (!hasNotified.current) {
        hasNotified.current = true
        onReady()
      }
    }, 100)
    
    return () => clearTimeout(timer)
  }, [onReady])
  
  return (
    <>
      <ChristmasTree />
      <Ground />
      <Snowfall />
    </>
  )
}

// 画廊模式蒙版组件已移除 - 使用 3D 场景中的黑色平面代替
// 这样可以确保照片在黑幕上方正确显示

function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [isSceneReady, setIsSceneReady] = useState(false)
  const isMobileDevice = isMobile()
  
  const handleLoadComplete = useCallback(() => {
    setIsLoading(false)
  }, [])
  
  // 场景准备就绪回调
  const handleSceneReady = useCallback(() => {
    setIsSceneReady(true)
  }, [])
  
  // 如果正在加载，显示加载界面
  if (isLoading) {
    return <LoadingScreen onLoadComplete={handleLoadComplete} />
  }
  
  return (
    <HandGestureProvider>
      <div className="w-screen h-screen bg-black">
        {/* 背景音乐 */}
        <BackgroundMusic />
        
        {/* 场景加载遮罩 - 防止黑屏 */}
        {!isSceneReady && (
          <div className="fixed inset-0 z-40 bg-[#0a0a1a] flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-4 animate-pulse">🎄</div>
              <p className="text-white/60 text-sm">正在渲染场景...</p>
            </div>
          </div>
        )}
        
        {/* 3D Canvas */}
        <Canvas
          camera={{
            position: getInitialCameraPosition(),
            fov: isMobileDevice ? 60 : SCENE_CONFIG.camera.fov, // 移动端使用更大的FOV
            near: SCENE_CONFIG.camera.near,
            far: SCENE_CONFIG.camera.far,
          }}
          gl={{
            antialias: true,
            alpha: false,
            powerPreference: 'high-performance',
          }}
          dpr={[1, 2]}
        >
          {/* 环境光照 - 增加亮度 */}
          <ambientLight intensity={0.3} />
          <directionalLight
            position={[10, 20, 10]}
            intensity={0.8}
            castShadow
            shadow-mapSize={[2048, 2048]}
          />
          <pointLight position={[0, 10, 0]} intensity={1.5} color="#FFD700" />
          <pointLight position={[5, 5, 5]} intensity={0.5} color="#FFD700" />
          <pointLight position={[-5, 5, -5]} intensity={0.5} color="#FFD700" />
          
          {/* 星空背景 */}
          <color attach="background" args={['#0a0a1a']} />
          <Stars
            radius={100}
            depth={50}
            count={5000}
            factor={4}
            saturation={0}
            fade
            speed={1}
          />
          
          {/* 环境贴图 - 为金属材质提供反射 */}
          <Environment
            files="/photos/warm_reception_dinner_2k.exr"
            background={false}
          />
          
          {/* 场景内容 */}
          <Suspense fallback={<LoadingFallback />}>
            <SceneContent onReady={handleSceneReady} />
          </Suspense>
          
          {/* 后期处理 */}
          <EffectComposer>
            <Bloom
              intensity={BLOOM_CONFIG.intensity}
              luminanceThreshold={BLOOM_CONFIG.threshold}
              luminanceSmoothing={0.9}
              mipmapBlur
            />
          </EffectComposer>
          
          {/* 摄像机控制 */}
          <CameraController />
          
          {/* 画廊模式点击处理 */}
          <GalleryClickHandler />
          
          {/* 通用手势处理（移动端和电脑端） */}
          <GestureHandler />
          
          {/* 动画控制器 */}
          <AnimationController />
        </Canvas>
        
        {/* UI 覆盖层 */}
        <UIOverlay />
      </div>
    </HandGestureProvider>
  )
}

export default App